import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useARC } from "./ARC/ARCProvider";
import { Ros, Service } from "roslib";
import { isHttpSecure } from "lib/utils/utils";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";

const InspectionContext = createContext();

export const useInspection = () => {
    const context = useContext(InspectionContext);
    if (!context) {
        throw new Error("useInspection must be used within InspectionProvider");
    }
    return context;
}

const INSPECTION_STORAGE_URL = `${isHttpSecure() ? "https" : "http"}://localhost:8080`;

const InspectionProvider = ({ children }) => {
    const { controlSocket, selectedARC } = useARC();
    const isSecure = useIsHttpSecure();

    const ros = controlSocket?.socket;
    const arcHost = selectedARC?.host;

    // Capture state - tracks the captured image
    const [captureData, setCaptureData] = useState({
        objectId: null,
        imageUrl: null,
        width: 0,
        height: 0,
        encoding: '',
        status: 'idle'  // 'idle' | 'capturing' | 'ready' | 'error'
    });

    // Inspection state - tracks the AI inspection results
    const [inspectionData, setInspectionData] = useState({
        annotatedObjectId: null,
        annotatedImageUrl: null,
        resultMessage: '',
        status: 'idle'  // 'idle' | 'inspecting' | 'complete' | 'error'
    });

    const [showInspectionPopup, setShowInspectionPopup] = useState(false);

    /**
     * Step 1: Capture image from gimbal
     */
    const captureImage = useCallback(async () => {
        if (!ros) {
            console.error("ROS connection not available");
            return;
        }

        // Reset states
        setCaptureData({
            objectId: null,
            imageUrl: null,
            width: 0,
            height: 0,
            encoding: '',
            status: 'capturing'
        });
        setInspectionData({
            annotatedObjectId: null,
            annotatedImageUrl: null,
            resultMessage: '',
            status: 'idle'
        });
        setShowInspectionPopup(true);

        console.log("Calling /gimbal/capture_image service...");

        try {
            // Create service client
            const captureService = new Service({
                ros: ros,
                name: "/gimbal/capture_image",
                serviceType: "r2c2_gimbal_interfaces/srv/CaptureImage",
            });

            // Call service
            const request = {};  // Empty request

            captureService.callService(
                request,
                (response) => {
                    console.log("Capture service response:", response);

                    if (!response.success) {
                        setCaptureData(prev => ({
                            ...prev,
                            status: 'error'
                        }));
                        console.error("Capture failed:", response.message);
                        return;
                    }

                    // Construct image URL from object_id using ARC's host
                    const imageUrl = arcHost
                        ? `${isSecure ? "https" : "http"}://${arcHost}:8080/captures/${response.object_id}`
                        : `${INSPECTION_STORAGE_URL}/captures/${response.object_id}`;

                    // Update capture state with successful capture
                    setCaptureData({
                        objectId: response.object_id,
                        imageUrl: imageUrl,
                        width: response.image.width,
                        height: response.image.height,
                        encoding: response.image.encoding,
                        status: 'ready'
                    });

                    console.log("Image captured successfully. Object ID:", response.object_id);
                },
                (error) => {
                    console.error("Capture service error:", error);
                    setCaptureData(prev => ({
                        ...prev,
                        status: 'error'
                    }));
                }
            );
        } catch (error) {
            console.error("Failed to call capture service:", error);
            setCaptureData(prev => ({
                ...prev,
                status: 'error'
            }));
        }
    }, [ros, arcHost, isSecure]);

    /**
     * Step 2: Inspect the captured image with user's question
     * @param {string} question - User's question or object name
     * @param {string} inspectionType - "detection" or "questCap"
     */
    const inspectCaptured = useCallback(async (question, inspectionType = "questCap") => {
        if (!ros) {
            console.error("ROS connection not available");
            return;
        }

        if (!captureData.objectId) {
            console.error("No captured image available");
            return;
        }

        // Set inspecting status
        setInspectionData({
            annotatedObjectId: null,
            annotatedImageUrl: null,
            resultMessage: '',
            status: 'inspecting'
        });

        console.log("Calling /inspection_moondream/inspect_captured service...");
        console.log("  capture_object_id:", captureData.objectId);
        console.log("  inspection_type:", inspectionType);
        console.log("  additional_data:", question);

        try {
            // Create service client
            const inspectService = new Service({
                ros: ros,
                name: "/inspection_moondream/inspect_captured",
                serviceType: "inspection_flow_action_interfaces/srv/InspectCaptured",
            });

            // Call service
            const request = {
                capture_object_id: captureData.objectId,
                inspection_type: inspectionType,
                additional_data: question || ""
            };

            inspectService.callService(
                request,
                (response) => {
                    console.log("Inspect service response:", response);

                    if (!response.success) {
                        setInspectionData({
                            annotatedObjectId: null,
                            annotatedImageUrl: null,
                            resultMessage: `Error: ${response.message}`,
                            status: 'error'
                        });
                        return;
                    }

                    // Construct annotated image URL if available using ARC's host
                    let annotatedImageUrl = null;
                    if (response.annotated_object_id) {
                        annotatedImageUrl = arcHost
                            ? `${isSecure ? "https" : "http"}://${arcHost}:8080/annotated/${response.annotated_object_id}`
                            : `${INSPECTION_STORAGE_URL}/annotated/${response.annotated_object_id}`;
                    }

                    // Update inspection state with results
                    setInspectionData({
                        annotatedObjectId: response.annotated_object_id,
                        annotatedImageUrl: annotatedImageUrl,
                        resultMessage: response.message,
                        status: 'complete'
                    });

                    console.log("Inspection completed:", response.message);
                },
                (error) => {
                    console.error("Inspect service error:", error);
                    setInspectionData({
                        annotatedObjectId: null,
                        annotatedImageUrl: null,
                        resultMessage: `Error: ${error}`,
                        status: 'error'
                    });
                }
            );
        } catch (error) {
            console.error("Failed to call inspect service:", error);
            setInspectionData({
                annotatedObjectId: null,
                annotatedImageUrl: null,
                resultMessage: `Error: ${error.message}`,
                status: 'error'
            });
        }
    }, [ros, captureData.objectId, arcHost, isSecure]);

    /**
     * Reset and start over - capture a new image
     */
    const resetCapture = useCallback(() => {
        captureImage();
    }, [captureImage]);

    /**
     * Ask another question about the same captured image
     */
    const askAnotherQuestion = useCallback(() => {
        // Reset only inspection data, keep capture data
        setInspectionData({
            annotatedObjectId: null,
            annotatedImageUrl: null,
            resultMessage: '',
            status: 'idle'
        });
    }, []);

    /**
     * Close the inspection popup
     */
    const closeInspectionPopup = useCallback(() => {
        setShowInspectionPopup(false);
        // Reset states after a short delay
        setTimeout(() => {
            setCaptureData({
                objectId: null,
                imageUrl: null,
                width: 0,
                height: 0,
                encoding: '',
                status: 'idle'
            });
            setInspectionData({
                annotatedObjectId: null,
                annotatedImageUrl: null,
                resultMessage: '',
                status: 'idle'
            });
        }, 300);
    }, []);

    const value = {
        // Capture data
        captureData,
        // Inspection data
        inspectionData,
        // Actions
        captureImage,
        inspectCaptured,
        resetCapture,
        askAnotherQuestion,
        // UI state
        showInspectionPopup,
        closeInspectionPopup,
    };

    return <InspectionContext.Provider value={value}>{children}</InspectionContext.Provider>;
};

export default InspectionProvider;
